import * as React from "react";
import { observer } from "mobx-react-lite";
import { mk8sDryRun } from "../../../../mobxStores/dryRun/mk8s";
import { mk8sMobx } from "../../../../mst/kinds/mk8s/mk8s";
import { Mk8sDraftMobx } from "../../../../mst/stores/mk8s.draft";
import { homeLink, request } from "../../../../services/cpln";
import { DryRunAlert } from "../../dryRunAlert";
import { PlusCircle } from "react-feather";
import { NGKindSelect } from "../../../../newcomponents/select/ngkindselect";
import { NGFormLabel } from "../../../../newcomponents/text/formLabel";
import NGAlert from "../../../../newcomponents/alert";
import { NGProviderCreateOpaqueSecret } from "../../ngProviderCreateOpaqueSecret";
import { NGProviderBrowser } from "../../ngProviderBrowser";
import { NGInputListMst } from "../../../../newcomponents/inputList/inputListMst";
import { NGInput } from "../../../../newcomponents/input/input";
import { getDryRunErrorText } from "../../getDryRunErrorText";
import { NGButton } from "../../../../newcomponents/button/Button";
import { NGLabel } from "../../../../newcomponents/text/label";

interface Props {
  mk8s: mk8sMobx;
  mk8sDraft: Mk8sDraftMobx;
}
const ProviderPaperspaceRaw: React.FC<Props> = ({ mk8s, mk8sDraft }) => {
  const p = mk8sDraft.provider_paperspace!;

  const [browserKey, setBrowserKey] = React.useState("");

  const sharedDrivesDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: "spec.provider.paperspace.sharedDrives",
    paths: p.sharedDrives.items.map((_, idx) => `spec.provider.paperspace.sharedDrives[${idx}]`),
  });
  const userIdsDryRun = getDryRunErrorText({
    dryRunResponse: mk8sDryRun.response,
    path: "spec.provider.paperspace.userIds",
    paths: p.userIds.items.map((_, idx) => `spec.provider.paperspace.userIds[${idx}]`),
  });

  return (
    <>
      <div>
        <div className="flex gap-2 items-start">
          <div>
            <NGLabel>Region</NGLabel>
            <div className="flex items-center mb-4">
              <div>{p.region.value}</div>
            </div>
          </div>
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={p.region.setValue}
              path={"spec.provider.paperspace.region"}
            />
          </div>
        </div>

        <div className="mb-4">
          <NGFormLabel required invalid={!p.tokenSecretName}>
            Paperspace Token Secret
          </NGFormLabel>
          <div className="mb-2 flex items-start gap-2">
            <NGKindSelect
              style={{ width: 450 }}
              value={p.tokenSecretName}
              invalid={!p.tokenSecretName}
              onChange={(value) => p.setTokenSecretName(value)}
              kind={"secret"}
              queries={[{ property: "type", value: "opaque" }]}
              buttons={[
                {
                  title: "Create",
                  render: () => <PlusCircle className="h-4" />,
                  onClick: () => setBrowserKey("createSecret"),
                },
              ]}
            />
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              path={"spec.provider.paperspace.tokenSecretLink"}
            />
          </div>
          <NGAlert
            style={{ width: 450 }}
            render={() => (
              <div className="flex flex-col text-sm">
                <span>An opaque secret that contains your Paperspace token.</span>
                <span>It will be used to manage servers in your Paperspace account.</span>
              </div>
            )}
          />
        </div>

        <div className="flex gap-2 items-start">
          <div>
            <NGLabel>Network ID</NGLabel>
            <div className="flex items-center mb-4">
              <div>{p.networkId.value}</div>
            </div>
          </div>
          <div className="mt-6">
            <DryRunAlert
              key={mk8sDryRun.key}
              canShow={true}
              dryRunResponse={mk8sDryRun.response}
              onFix={p.networkId.setValue}
              path={"spec.provider.paperspace.networkId"}
            />
          </div>
        </div>

        <NGInputListMst
          data={p.sharedDrives}
          className="mb-8"
          styles={{ container: { width: 450, minWidth: 450 } }}
          label="Shared Drives"
          labelRender={() => {
            return (
              <>
                <NGButton
                  className="ml-1"
                  size={"small"}
                  variant={"secondary"}
                  onClick={() => setBrowserKey("shared-drives")}
                >
                  Browse
                </NGButton>
              </>
            );
          }}
          firstInput={(i, index) => {
            let invalid = false;
            if (sharedDrivesDryRun.message.split("[")[1]?.split("]")[0] === String(index)) {
              invalid = true;
            }

            return <NGInput invalid={invalid} value={i.firstValue} onChange={(e) => i.setFirstValue(e.target.value)} />;
          }}
          invalid={sharedDrivesDryRun.severity === "error"}
          error={sharedDrivesDryRun.severity === "error" ? sharedDrivesDryRun.message : ""}
          warning={sharedDrivesDryRun.severity === "error" ? "" : sharedDrivesDryRun.message}
        />
        <NGInputListMst
          data={p.userIds}
          className="mb-8"
          styles={{ container: { width: 450, minWidth: 450 } }}
          label="User Ids"
          labelRender={() => {
            return (
              <>
                <NGButton className="ml-1" size={"small"} variant={"secondary"} onClick={() => setBrowserKey("users")}>
                  Browse
                </NGButton>
              </>
            );
          }}
          firstInput={(i, index) => {
            let invalid = false;
            if (userIdsDryRun.message.split("[")[1]?.split("]")[0] === String(index)) {
              invalid = true;
            }

            return <NGInput invalid={invalid} value={i.firstValue} onChange={(e) => i.setFirstValue(e.target.value)} />;
          }}
          invalid={userIdsDryRun.severity === "error"}
          error={userIdsDryRun.severity === "error" ? userIdsDryRun.message : ""}
          warning={userIdsDryRun.severity === "error" ? "" : userIdsDryRun.message}
        />
      </div>

      {browserKey === "createSecret" ? (
        <NGProviderCreateOpaqueSecret
          onOk={(secretName: string) => p.setTokenSecretName(secretName)}
          onClose={() => setBrowserKey("")}
          title={"Create Paperspace Secret"}
          dataLabel={"Paperspace Token"}
        />
      ) : null}
      {browserKey === "shared-drives" ? (
        <NGProviderBrowser
          title="Browse Paperspace Shared Drives"
          initialValue={p.sharedDrives.items.map((i) => i.firstValue)}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "shared-drives",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.items;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (p.sharedDrives.items.some((i) => i.firstValue === value)) {
                continue;
              }
              p.sharedDrives.add();
              const i = p.sharedDrives.items[p.sharedDrives.items.length - 1];
              i.setFirstValue(value);
            }
          }}
          onClose={() => setBrowserKey("")}
          labels={["Name"]}
          getData={(item: any) => [String(item.name), item.name]}
        />
      ) : null}
      {browserKey === "users" ? (
        <NGProviderBrowser
          title="Browse Paperspace Users"
          initialValue={p.userIds.items.map((i) => i.firstValue)}
          fetchData={async () => {
            const { data } = await request({
              service: "api",
              url: homeLink("mk8s") + "/-discover",
              method: "post",
              body: {
                objectType: "users",
                cluster: mk8sDraft.asObject,
              },
            });
            return data.items;
          }}
          multipleChoice
          onOk={(_value) => {
            const values: string[] = _value as any;
            for (const value of values) {
              if (p.userIds.items.some((i) => i.firstValue === value)) {
                continue;
              }
              p.userIds.add();
              const i = p.userIds.items[p.userIds.items.length - 1];
              i.setFirstValue(value);
            }
          }}
          onClose={() => setBrowserKey("")}
          labels={["Id", "Email", "Full Name"]}
          getData={(item: any) => [
            String(item.user.id),
            item.user.id,
            item.user.email,
            item.user.firstName + " " + item.user.lastName,
          ]}
        />
      ) : null}
    </>
  );
};

export const ProviderPaperspace = observer(ProviderPaperspaceRaw);
